/**
 * 운영자(SYSTEM_ADMIN) 사용자 유지보수 API — 회사를 골라 **비밀번호만** 재설정한다.
 *
 * 서버 SSOT: `apps/chat-server/src/modules/system/system-user-maintenance.routes.ts`
 * 설계: `docs/attendance-round2-sample.html` 화면 L · 제안 1 · 결정 ⑧.
 *
 * BFF 경로: admin-frontend `/api/system/*` → chat-server `/api/v1/system/*`
 * (통로는 `admin/backend/src/routes/proxy.ts` 의 ALLOWED_PREFIXES 에 열려 있다 — 없으면 404).
 *
 * ⚠ 여기 오는 사용자 정보에는 **연락처·직위·생일·근태가 없다.** 화면이 가리는 것이 아니라
 *   서버가 보내지 않는다 — 새 칸이 필요해 보이면 그 전에 "운영자가 봐도 되는가" 를 먼저 묻는다.
 */
import { apiClient } from './client';

export interface SystemTenant {
  id: string;
  code: string;
  name: string;
  domain: string | null;
  status: string;
}

export interface SystemMaintenanceUser {
  id: string;
  name: string;
  email: string;
  employeeNumber: string | null;
  role: string;
  status: string;
  isActive: boolean;
  /** 비밀번호가 설정돼 있는지 — 재설정 뒤에는 false 가 된다(사용자가 새로 정할 때까지). */
  hasPassword: boolean;
}

export interface SystemUserListResult {
  institution: { id: string; name: string; code: string; status: string };
  total: number;
  users: SystemMaintenanceUser[];
  nextCursor: string | null;
}

export type ResetMode = 'temp_password' | 'invite_link';

export interface SystemResetResult {
  userId: string;
  email: string;
  name: string;
  role: string;
  employeeNumber: string | null;
  invite: {
    kind: ResetMode;
    /** invite_link 일 때만. 한 번만 보여 준다(서버에 평문이 없다). */
    token?: string;
    /** temp_password 일 때만. 한 번만 보여 준다. */
    tempPassword?: string;
    expiresAt: string;
  };
  notified: { inApp: boolean; mail: 'sent' | 'skipped_not_configured' | 'failed' };
}

/** 회사 목록 — 이미 있는 운영자 테넌트 목록을 그대로 쓴다(새 경로 0). */
export async function listSystemTenants(): Promise<SystemTenant[]> {
  const { data } = await apiClient.get<{ items: SystemTenant[] }>('/system/tenants', {
    params: { limit: 100 },
  });
  return Array.isArray(data?.items) ? data.items : [];
}

/** 회사별 인원수 — 회사마다 부르지 않고 한 번에 받는다(회사 수만큼 왕복하지 않기 위해). */
export async function listSystemUserCounts(): Promise<Record<string, number>> {
  const { data } = await apiClient.get<{ counts: Record<string, number> }>('/system/user-counts');
  return data?.counts ?? {};
}

export async function listSystemUsers(params: {
  institutionId: string;
  search?: string;
  limit?: number;
}): Promise<SystemUserListResult> {
  const { data } = await apiClient.get<SystemUserListResult>('/system/users', {
    params: {
      institutionId: params.institutionId,
      ...(params.search ? { search: params.search } : {}),
      limit: params.limit ?? 100,
    },
  });
  return { ...data, users: Array.isArray(data?.users) ? data.users : [] };
}

/** 사유는 필수(10자 이상)이고 **본인 통지에 그대로 실린다** — 화면이 그 사실을 미리 알린다. */
export async function resetSystemUserPassword(
  userId: string,
  body: { institutionId: string; reason: string; mode: ResetMode },
): Promise<SystemResetResult> {
  const { data } = await apiClient.post<SystemResetResult>(
    `/system/users/${userId}/password/reset`,
    body,
  );
  return data;
}
